import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { Table, Card, Progress, Tag, Image, Tooltip, Typography, Select, DatePicker, Button, Space, message } from 'antd';
import { getAllvideos } from '../../graphql/queries';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getRowLinkProps, withRowLink } from '../../utils/rowLink';
import './Videos.css';

const { Paragraph } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const availableModels = [
  { value: 'WIRO_PX', label: 'W_TXT2V_v2' },
  { value: 'WIRO_KLING', label: 'W_KLING_v1' },
  { value: 'WIRO_MINIMAX', label: 'W_MINI_v1' },
  { value: 'FAL', label: 'W_FAL_v1' }
];


const Videos = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pagination, setPagination] = useState({
    current: parseInt(searchParams.get('page')) || 1,
    pageSize: parseInt(searchParams.get('pageSize')) || 10,
  });
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [filterNullPrompts, setFilterNullPrompts] = useState(false);
  const [selectedType, setSelectedType] = useState(null);

  const { loading, data, refetch } = useQuery(getAllvideos, {
    variables: {
      page: pagination.current,
      pageSize: pagination.pageSize,
      status: selectedStatus,
      model: selectedModel,
      startDate: dateRange?.[0]?.format('YYYY-MM-DD') ?? null,
      endDate: dateRange?.[1]?.format('YYYY-MM-DD') ?? null,
      type: selectedType && selectedType !== 'all' ? selectedType : null
    },
  });

  useEffect(() => {
    const params = new URLSearchParams();
    params.set('page', pagination.current.toString());
    params.set('pageSize', pagination.pageSize.toString());
    if (selectedStatus) params.set('status', selectedStatus);
    if (selectedModel) params.set('model', selectedModel);
    if (dateRange && dateRange[0] && dateRange[1]) {
      params.set('startDate', dateRange[0].format('YYYY-MM-DD'));
      params.set('endDate', dateRange[1].format('YYYY-MM-DD'));
    }
    if (selectedType) params.set('type', selectedType);
    setSearchParams(params);
  }, [pagination, selectedStatus, selectedModel, dateRange, setSearchParams]);

  const getStatusColor = (status) => {
    const colors = {
      SUCCEEDED: 'green',
      FAILED: 'red',
      RUNNING: 'blue',
      IN_QUE: 'orange',
      PENDING: 'purple',
      THROTTLED: 'gold',
      MODERATION: 'magenta'
    };
    return colors[status] || 'default';
  };

  const columns = [
    {
      title: 'Thumbnail',
      dataIndex: 'thumbnailUrl',
      key: 'thumbnailUrl',
      width: 90,
      render: (url) => (
        <Image
          src={url}
          alt="Thumbnail"
          style={{ width: '70px', height: '70px', objectFit: 'cover' }}
          preview={{
            maskClassName: 'customize-mask',
            mask: <div>View</div>
          }}
        />
      ),
    },
    {
      title: 'Prompt',
      dataIndex: 'prompt',
      key: 'prompt',
      width: 180,
      render: (text) => (
        <Tooltip title={text} placement="topLeft">
          <Paragraph ellipsis={{ rows: 1 }} style={{ marginBottom: 0, maxWidth: 160 }}>
            {text}
          </Paragraph>
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const displayText = {
          SUCCEEDED: 'Succeeded',
          FAILED: 'Failed',
          RUNNING: 'Running',
          IN_QUE: 'In Queue',
          PENDING: 'Pending',
          THROTTLED: 'Throttled',
          MODERATION: 'Moderation'
        };
        return (
          <Tag color={getStatusColor(status)}>
            {displayText[status] || status}
          </Tag>
        );
      },
      filterDropdown: () => (
        <Select
          style={{ width: 120, margin: 8 }}
          value={selectedStatus}
          onChange={(value) => {
            setSelectedStatus(value);
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
          allowClear
          placeholder="Filter status"
        >
          <Option value="RUNNING">Running</Option>
          <Option value="FAILED">Failed</Option>
          <Option value="SUCCEEDED">Succeeded</Option>
          <Option value="IN_QUE">In Queue</Option>
          <Option value="PENDING">Pending</Option>
          <Option value="THROTTLED">Throttled</Option>
          <Option value="MODERATION">Moderation</Option>
        </Select>
      ),
      filtered: !!selectedStatus,
    },
    {
      title: 'Progress',
      dataIndex: 'progressRatio',
      key: 'progressRatio',
      width: 200,
      render: (progress) => (
        <Progress 
          percent={progress || 0}
          size="small" 
          status={progress === 100 ? 'success' : 'active'}
          strokeColor={{
            '0%': '#108ee9',
            '100%': '#87d068',
          }}
        />
      ),
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      width: 150,
      render: (model) => model,
      filterDropdown: () => (
        <Select
          style={{ width: 150, margin: 8 }}
          value={selectedModel}
          onChange={(value) => {
            setSelectedModel(value);
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
          allowClear
          placeholder="Filter model"
        >
          {availableModels.map(model => (
            <Option key={model.value} value={model.value}>
              {model.value}
            </Option>
          ))}
        </Select>
      ),
      filtered: !!selectedModel,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      filterDropdown: () => (
        <Select
          style={{ width: 150, margin: 8 }}
          value={selectedType}
          onChange={(value) => {
            setSelectedType(value);
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
          allowClear
          placeholder="Filter type"
        >
          <Option value="img2Video">img2Video</Option>
          <Option value="text2Video">text2Video</Option>
          <Option value="toolPredict">toolPredict</Option>
        </Select>
      ),
  filtered: !!selectedType && selectedType !== 'all',},
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date) => new Date(date).toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
    },
  ];

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleRowClick = (record) => {
    navigate(`/video/${record.id}`);
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    // Reset to first page when date range changes
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFilterNullPrompts = () => {
    setFilterNullPrompts(!filterNullPrompts);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const exportToCSV = async () => {
    try {
      message.loading('Fetching all data for export...', 0);
      
      // Fetch all data without pagination for CSV export
      const result = await refetch({
        page: 1,
        pageSize: 10000, // Large number to get all data
        status: selectedStatus,
        model: selectedModel,
        startDate: dateRange?.[0]?.format('YYYY-MM-DD') ?? null,
        endDate: dateRange?.[1]?.format('YYYY-MM-DD') ?? null,
      });

      message.destroy(); // Clear loading message

      if (!result.data?.getAllVideos?.videos || result.data.getAllVideos.videos.length === 0) {
        message.warning('No data to export');
        return;
      }

      // Filter videos based on current filters
      let videosToExport = result.data.getAllVideos.videos;
      
      if (filterNullPrompts) {
        videosToExport = videosToExport.filter(video => video.prompt && video.prompt.trim() !== '');
      }

      message.success(`Exporting ${videosToExport.length} videos to CSV...`);

      // Create CSV content with only prompt, url, and createdAt
      const csvContent = [
        ['Prompt', 'URL', 'Created At'],
        ...videosToExport.map(video => [
          video.prompt || '',
          video.url || '',
          new Date(video.createdAt).toLocaleString('tr-TR')
        ])
      ].map(row => row.map(field => `"${field}"`).join(',')).join('\n');

      // Create and download CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `videos_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success(`Successfully exported ${videosToExport.length} videos!`);
    } catch (error) {
      message.destroy(); // Clear loading message
      message.error('Failed to export data: ' + error.message);
    }
  };

  return (
    <div className="videos-container">
      <Card title="Videos">
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
          <RangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            placeholder={['Start Date', 'End Date']}
            style={{ width: 300 }}
            showTime={false}
            format="YYYY-MM-DD"
          />
          <Button
            type={filterNullPrompts ? 'primary' : 'default'}
            onClick={handleFilterNullPrompts}
            size="small"
            style={{ height: '32px' }}
          >
            {filterNullPrompts ? 'Show All Prompts' : 'Filter Null Prompts'}
          </Button>
          <Button
            type="primary"
            onClick={exportToCSV}
            size="small"
            style={{ height: '32px' }}
            disabled={!dateRange || !dateRange[0] || !dateRange[1] || !data?.getAllVideos?.videos || data.getAllVideos.videos.length === 0}
          >
            Export CSV
          </Button>
        </div>
        <Table
          columns={withRowLink(columns, (record) => `/video/${record.id}`, { exclude: ['thumbnailUrl'] })}
          dataSource={
            filterNullPrompts 
              ? (data?.getAllVideos?.videos || []).filter(video => video.prompt && video.prompt.trim() !== '')
              : (data?.getAllVideos?.videos || [])
          }
          loading={loading}
          rowKey="id"
          pagination={{
            ...pagination,
            total: data?.getAllVideos?.totalCount,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} videos`,
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            ...getRowLinkProps(`/video/${record.id}`, () => handleRowClick(record)),
          })}
        />
      </Card>
    </div>
  );
};

export default Videos;